import { useMemo } from 'react';
import { Stat, StatGroup, BarList, Timeline, EmptyState } from '@vnetwork/ui/data';
import { PieChart, Chart } from '@vnetwork/ui/charts';
import type { DashboardStats } from '@/api/capacities';
import { formatMoney } from '@/lib/format';
import { CapacitySectionCard } from './capacity-section-card';
import {
  toPieSlices,
  toBarRows,
  toSupplierBars,
  toTrendRows,
  toWarrantyCards,
  toRecentTimeline,
} from './physical-charts';

const GRID_2: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--vn-space-4)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
};

/**
 * The Physical Inventory sections beyond the base status/location charts:
 * inventory value, warranty alerts, category donuts, suppliers, age
 * distribution, process trends and recent activity. Read-only presentational.
 */
export function PhysicalExtraSections({ data }: { data: DashboardStats }) {
  const equipmentByCategory = useMemo(() => toPieSlices(data.equipment_by_category), [data]);
  const componentsByCategory = useMemo(() => toPieSlices(data.components_by_category), [data]);
  const ageBars = useMemo(() => toBarRows(data.equipment_age_distribution), [data]);
  const supplierBars = useMemo(() => toSupplierBars(data.top_suppliers), [data]);
  const trendRows = useMemo(() => toTrendRows(data.process_trend_monthly), [data]);
  const warrantyCards = useMemo(() => toWarrantyCards(data.warranty_expiring), [data]);
  const recentItems = useMemo(() => toRecentTimeline(data.recent_processes), [data]);

  return (
    <>
      {/* Inventory value headline */}
      <StatGroup columns={1}>
        <Stat
          label="Total Inventory Value"
          value={formatMoney(data.total_inventory_value)}
          hint="Estimated value across all equipment & components"
        />
      </StatGroup>

      {/* Warranty expiration alerts */}
      <CapacitySectionCard
        title="Warranty Expiration Alerts"
        description="Equipment approaching or past warranty expiry"
      >
        <StatGroup columns={5}>
          {warrantyCards.map((w) => (
            <Stat key={w.key} label={w.label} value={w.value} dotColor={w.dotColor} />
          ))}
        </StatGroup>
      </CapacitySectionCard>

      <div style={GRID_2}>
        <CapacitySectionCard title="Equipment by Category" description="Equipment count per category">
          {equipmentByCategory.length > 0 ? (
            <PieChart donut height={260} data={equipmentByCategory} valueFormatter={(v) => `${v ?? 0}`} />
          ) : (
            <EmptyState size="sm" title="No category data" />
          )}
        </CapacitySectionCard>

        <CapacitySectionCard title="Components by Category" description="Component count per category">
          {componentsByCategory.length > 0 ? (
            <PieChart donut height={260} data={componentsByCategory} valueFormatter={(v) => `${v ?? 0}`} />
          ) : (
            <EmptyState size="sm" title="No category data" />
          )}
        </CapacitySectionCard>
      </div>

      <div style={GRID_2}>
        <CapacitySectionCard
          title="Top Suppliers by Equipment Count"
          description="Suppliers ranked by equipment supplied"
        >
          {supplierBars.length > 0 ? (
            <BarList items={supplierBars} />
          ) : (
            <EmptyState size="sm" title="No supplier data" />
          )}
        </CapacitySectionCard>

        <CapacitySectionCard title="Equipment Age Distribution" description="Equipment grouped by age bracket">
          {ageBars.length > 0 ? <BarList items={ageBars} /> : <EmptyState size="sm" title="No age data" />}
        </CapacitySectionCard>
      </div>

      <CapacitySectionCard
        title="Process Trends (Last 6 Months)"
        description="Monthly import / export / transfer volume"
      >
        {trendRows.length > 0 ? (
          <Chart
            type="bar"
            stacked
            height={300}
            data={trendRows}
            xKey="label"
            series={[
              { key: 'import', label: 'Import', color: 'var(--vn-success-solid)' },
              { key: 'export', label: 'Export', color: 'var(--vn-warning-solid)' },
              { key: 'transfer', label: 'Transfer', color: 'var(--vn-info-solid)' },
            ]}
            showLegend
            valueFormatter={(v) => `${v ?? 0}`}
          />
        ) : (
          <EmptyState size="sm" title="No process trend data" />
        )}
      </CapacitySectionCard>

      <CapacitySectionCard
        title="Recent Process Activity"
        description="Latest import / export / transfer records"
      >
        {recentItems.length > 0 ? (
          <Timeline items={recentItems} emptyText="No recent processes" />
        ) : (
          <EmptyState size="sm" title="No recent activity" />
        )}
      </CapacitySectionCard>
    </>
  );
}
